import React, { useContext, useEffect, useState } from 'react';
import { useUserEvent } from '../hooks';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import PlanBadge, { PlanBadgeWithExpiry } from './PlanBadge';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { PlanService } from '../services/PlanService';
import { mergeUsageStatsWithPlanLimits } from '../config/planConfigResolver';
import { getPlanName } from '../config/paymentCatalog';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import TermosDeUso from './TermosDeUso';
import CentralAjuda from './CentralAjuda';
import {
  FaArrowLeft,
  FaBell,
  FaCalendarAlt,
  FaCrown,
  FaFileContract,
  FaHeadset,
  FaHistory,
  FaMobileAlt,
  FaPalette,
  FaPhone,
  FaQuestionCircle,
  FaSave,
  FaShieldAlt,
  FaTrash,
  FaUser,
} from 'react-icons/fa';
import { getDeviceFingerprint, getDeviceName, getLimiteDispositivos } from '../utils/deviceFingerprint';
import AuthenticatedEmailField, { getAuthenticatedEmail } from './AuthenticatedEmailField';
import './account-platform.css';

const ACCOUNT_SECTIONS = [
  { id: 'perfil', label: 'Dados pessoais', shortLabel: 'Perfil', icon: FaUser },
  { id: 'plano', label: 'Plano e uso', shortLabel: 'Plano', icon: FaCrown },
  { id: 'seguranca', label: 'Segurança', shortLabel: 'Segurança', icon: FaShieldAlt },
  { id: 'preferencias', label: 'Preferências', shortLabel: 'Preferências', icon: FaPalette },
  { id: 'ajuda', label: 'Ajuda', shortLabel: 'Ajuda', icon: FaHeadset },
  { id: 'legal', label: 'Legal', shortLabel: 'Legal', icon: FaFileContract },
];

export default function Perfil({ user, authenticatedUser = null, authIdentityLoading = false, onBack, onOpenPlans, initialSection = 'perfil' }) {
  useUserEvent({ userId: user?.id, tela: 'Perfil', evento: 'page_view', comDuração: true });
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const [activeSection, setActiveSection] = useState(
    ACCOUNT_SECTIONS.some(section => section.id === initialSection) ? initialSection : 'perfil'
  );
  const [nome, setNome] = useState('');
  const identityEmail = getAuthenticatedEmail(authenticatedUser);
  const [telefone, setTelefone] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  
  // Estados para plano
  const { plano, limites, loading: planLoading } = usePlanLimits(user?.id);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [verTermos, setVerTermos] = useState(false);
  const [verAjuda, setVerAjuda] = useState(false);

  // Estados para dispositivos
  const [dispositivos, setDispositivos] = useState([]);
  const [loadingDispositivos, setLoadingDispositivos] = useState(false);
  const fingerprintAtual = getDeviceFingerprint();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const nextSection = ACCOUNT_SECTIONS.some(section => section.id === initialSection) ? initialSection : 'perfil';
    setActiveSection(nextSection);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [initialSection]);

  // Carrega dados do usuário da tabela 'usuarios'
  useEffect(() => {
    // Preenche imediatamente com os dados já carregados no App.js
    if (user?.nome !== undefined) {
      setNome(user.nome || '');
      setTelefone(user.telefone || '');
      setDataNascimento(user.data_nascimento ? user.data_nascimento.substring(0, 10) : '');
    }

    // Busca atualizada do banco (garante dados frescos)
    async function fetchUsuario() {
      if (!user?.id) return;
      const { data, error } = await supabase
        .from('usuarios')
        .select('nome, telefone, data_nascimento')
        .eq('id', user.id)
        .single();
      if (error) {
        console.error('Erro ao carregar dados do perfil:', error.message);
        return;
      }
      if (data) {
        setNome(data.nome || '');
        setTelefone(data.telefone || '');
        setDataNascimento(data.data_nascimento ? data.data_nascimento.substring(0, 10) : '');
      }
    }
    fetchUsuario();
  }, [user]);

  const statsExibicao = mergeUsageStatsWithPlanLimits(stats, limites);

  // Carrega estatísticas de uso
  useEffect(() => {
    async function fetchStats() {
      if (!user?.id) return;
      setStatsLoading(true);
      setStatsError('');
      try {
        // Contagens reais, sempre sujeitas às políticas de acesso do usuário logado.
        const [propertyScope, alertsResult, sensorsResult] = await Promise.all([
          loadVisibleAuthorizedProperties(user.id, 'id, nome'),
          supabase.from('alertas').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('sensores_usuarios').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);

        if (alertsResult.error) throw alertsResult.error;
        if (sensorsResult.error) throw sensorsResult.error;

        const ownedProperties = (propertyScope.properties || [])
          .filter(property => property._accessRole === 'owner');
        const propertyIds = ownedProperties.map(property => property.id).filter(Boolean);
        const migratedPlotCount = ownedProperties
          .filter(property => property._usesPhysicalPlots)
          .reduce((total, property) => total + (property._operationalPlots?.length || 0), 0);
        const legacyPropertyIds = ownedProperties
          .filter(property => !property._usesPhysicalPlots)
          .map(property => property.id)
          .filter(Boolean);
        const fieldsResult = legacyPropertyIds.length
          ? await supabase.from('talhoes').select('id', { count: 'exact', head: true }).in('propriedade_id', legacyPropertyIds)
          : { count: 0, error: null };
        if (fieldsResult.error) throw fieldsResult.error;

        setStats({
          propriedades: { usado: propertyIds.length },
          talhoes: { usado: migratedPlotCount + (fieldsResult.count || 0) },
          alertas: { usado: alertsResult.count || 0 },
          sensores: { usado: sensorsResult.count || 0 },
        });
      } catch (err) {
        console.error('Erro ao carregar estatísticas reais:', err);

        // Mantém a tela útil em instalações antigas nas quais alguma contagem
        // ainda depende da função consolidada do banco.
        const fallback = await PlanService.verificarMultiplosLimites(user.id, [
          'propriedades', 'talhoes', 'alertas', 'sensores'
        ]);
        if (fallback.success && Object.values(fallback.data || {}).some(item => item?.usado != null)) {
          setStats(fallback.data);
          setStatsError('Algumas contagens foram obtidas pelo consolidado do plano.');
        } else {
          setStats(null);
          setStatsError('Não foi possível consultar a utilização da conta agora. Tente novamente em instantes.');
        }
      } finally {
        setStatsLoading(false);
      }
    }
    
    fetchStats();
  }, [user]);

  // Carrega dispositivos cadastrados e auto-registra o atual se necessário
  useEffect(() => {
    async function fetchDispositivos() {
      if (!user?.id) return;
      const { data } = await supabase
        .from('dispositivos_usuario')
        .select('id, device_name, device_fingerprint, criado_em, ultimo_acesso')
        .eq('user_id', user.id)
        .order('criado_em', { ascending: true });

      const lista = data || [];

      // Auto-registra o dispositivo atual se ainda não estiver cadastrado
      const fingerprint = getDeviceFingerprint();
      const jaExiste = lista.some(d => d.device_fingerprint === fingerprint);
      if (!jaExiste) {
        const deviceName = getDeviceName();
        const { data: novo, error: errUpsert } = await supabase
          .from('dispositivos_usuario')
          .upsert(
            { user_id: user.id, device_fingerprint: fingerprint, device_name: deviceName },
            { onConflict: 'user_id,device_fingerprint', ignoreDuplicates: true }
          )
          .select()
          .single();
        if (novo) lista.push(novo);
      }

      setDispositivos(lista);
    }
    fetchDispositivos();
  }, [user]);

  const revogarDispositivo = async (id) => {
    if (!window.confirm('Remover este dispositivo? Ele precisará ser recadastrado no próximo login.')) return;
    setLoadingDispositivos(true);
    await supabase.from('dispositivos_usuario').delete().eq('id', id);
    setDispositivos(prev => prev.filter(d => d.id !== id));
    setLoadingDispositivos(false);
  };

  // Carrega histórico de planos
  const carregarHistorico = async () => {
    const resultado = await PlanService.getHistorico(user.id);
    if (resultado.success) {
      setHistorico(resultado.data);
      setMostrarHistorico(true);
    }
  };

  const inputStyle = {
    padding: '12px',
    borderRadius: 8,
    border: darkMode ? '1.5px solid #90caf9' : '1.5px solid #1976d2',
    width: '100%',
    fontSize: 16,
    outline: 'none',
    marginBottom: 12,
    background: darkMode ? '#232526' : '#f8fafc',
    color: darkMode ? '#f8fafc' : undefined,
    boxSizing: 'border-box',
    transition: 'background 0.2s, color 0.2s, border 0.2s',
  };

  // O e-mail vem da identidade autenticada e é exibido somente para consulta.
  // Alterações de e-mail exigem um fluxo próprio de confirmação e não devem ser
  // misturadas com a atualização dos dados pessoais.
  const handleSalvar = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);

    // Atualiza dados na tabela usuarios
    const { error: errorDb } = await supabase
      .from('usuarios')
      .update({ nome, telefone, data_nascimento: dataNascimento || null })
      .eq('id', user.id);

    setLoading(false);

    if (errorDb) {
      setError('Erro ao salvar dados: ' + errorDb.message);
    } else {
      setMessage('Dados salvos com sucesso!');
    }
  };

  return (
    <div className={`account-page-shell${darkMode ? ' is-dark' : ''}`} style={{
      maxWidth: 800,
      margin: '2rem auto',
      padding: '0 1rem'
    }}>
      <header className="account-profile-hero">
        <div className="account-avatar" aria-hidden="true">
          {(nome || identityEmail || 'U').trim().charAt(0).toUpperCase()}
        </div>
        <div className="account-profile-identity">
          <span>Minha conta</span>
          <h1>{nome || 'Complete seu perfil'}</h1>
          <p>{authIdentityLoading ? 'Carregando identidade…' : identityEmail || 'E-mail não informado'}</p>
        </div>
        <div className="account-profile-status">
          <FaShieldAlt /> Conta protegida
        </div>
      </header>

      <nav className="account-center-navigation" aria-label="Seções da Central da Conta">
        {ACCOUNT_SECTIONS.map(section => {
          const Icon = section.icon;
          const selected = activeSection === section.id;
          return (
            <button
              type="button"
              key={section.id}
              className={selected ? 'is-active' : ''}
              aria-current={selected ? 'page' : undefined}
              onClick={() => setActiveSection(section.id)}
            >
              <Icon /><span>{section.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Card de Informações do Plano */}
      {activeSection === 'plano' && plano && (
        <section className="account-card account-plan-overview" style={{
          background: darkMode ? '#232526' : '#fff',
          borderRadius: 16,
          boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px #1976d233',
          padding: '2rem 1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div className="account-card-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span className="account-eyebrow"><FaCrown /> Assinatura</span>
              <h2 style={{ color: darkMode ? '#ffd54f' : '#1976d2', margin: 0 }}>Meu plano</h2>
              <p>Recursos contratados e utilização da conta.</p>
            </div>
            <PlanBadgeWithExpiry 
              plano={plano.tipo}
              dataExpiracao={plano.dataExpiracao}
              size="large"
            />
          </div>

          {/* Trial Banner */}
          {plano.trial && (
            <div style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
              color: '#000',
              padding: '12px 16px',
              borderRadius: 12,
              marginBottom: '1.5rem',
              fontWeight: 600,
              textAlign: 'center'
            }}>
              🎉 Você está no período de avaliação do ClimoraX!
              {plano.dataExpiracao && (
                <span style={{ marginLeft: 8 }}>
                  ({PlanService.diasRestantes(plano.dataExpiracao)} dias restantes)
                </span>
              )}
            </div>
          )}

          {/* Estatísticas de Uso */}
          {statsLoading && (
            <div className="account-usage-status" role="status" aria-live="polite">
              Calculando a utilização real da conta…
            </div>
          )}
          {!statsLoading && statsError && (
            <div className="account-usage-status is-warning" role="status">
              {statsError}
            </div>
          )}
          {statsExibicao && (
            <div className="account-usage-section" style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                fontSize: 16, 
                fontWeight: 700, 
                marginBottom: 12,
                color: darkMode ? '#90caf9' : '#1976d2'
              }}>
                📊 Seu Uso Atual
              </h3>
              <p className="account-usage-scope">Os limites do plano consideram propriedades próprias. Áreas compartilhadas aceitas permanecem acessíveis sem duplicar o consumo.</p>
              <div className="account-usage-grid" style={{
                display: 'grid', 
                gridTemplateColumns: window.innerWidth > 600 ? 'repeat(2, 1fr)' : '1fr',
                gap: 12 
              }}>
                {Object.entries({
                  'Propriedades': statsExibicao.propriedades,
                  'Talhões': statsExibicao.talhoes,
                  'Alertas': statsExibicao.alertas,
                  'Sensores': statsExibicao.sensores
                }).map(([nome, stat]) => {
                  if (!stat) return null;
                  
                  const percentual = stat.percentual || 0;
                  const corBarra = percentual >= 80 ? '#f44336' : percentual >= 60 ? '#ff9800' : '#4caf50';
                  
                  return (
                    <div className="account-usage-item" key={nome} style={{
                      background: darkMode ? '#2a2a2a' : '#f8fafc',
                      padding: 12,
                      borderRadius: 10
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: 6,
                        fontSize: 13,
                        fontWeight: 600
                      }}>
                        <span>{nome}</span>
                        <span style={{ color: darkMode ? '#90caf9' : '#1976d2' }}>
                          {stat.ilimitado ? `${stat.usado} (Ilimitado)` : `${stat.usado} / ${stat.limite}`}
                        </span>
                      </div>
                      {!stat.ilimitado && (
                        <div style={{
                          background: darkMode ? '#444' : '#e2e8f0',
                          height: 6,
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: corBarra,
                            height: '100%',
                            width: `${percentual}%`,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Alerta de Limite */}
              {statsExibicao.propriedades && !statsExibicao.propriedades.ilimitado && statsExibicao.propriedades.percentual >= 80 && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#fff3cd',
                  color: '#856404',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600
                }}>
                  ⚠️ Você está próximo do limite! Considere fazer upgrade.
                </div>
              )}
            </div>
          )}

          {/* Ações */}
          <div className="account-plan-actions" style={{
            display: 'flex', 
            gap: 12,
            flexWrap: 'wrap'
          }}>
            {onOpenPlans && (
              <button type="button" className="account-secondary-action" onClick={onOpenPlans}>
                <FaCrown /> Comparar planos disponíveis
              </button>
            )}

            <button
              style={{
                padding: '12px 20px',
                background: darkMode ? '#444' : '#e2e8f0',
                color: darkMode ? '#fff' : '#1e293b',
                border: 'none',
                borderRadius: 10,
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
              onClick={carregarHistorico}
            >
              <FaHistory /> Ver Histórico
            </button>
          </div>

          {/* Histórico */}
          {mostrarHistorico && historico.length > 0 && (
            <div className="account-plan-history" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${darkMode ? '#444' : '#e2e8f0'}` }}>
              <h3 style={{ 
                fontSize: 16, 
                fontWeight: 700, 
                marginBottom: 12,
                color: darkMode ? '#90caf9' : '#1976d2'
              }}>
                📜 Histórico de Planos
              </h3>
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {historico.map((item) => (
                  <div className="account-history-row" key={item.id} style={{
                    background: darkMode ? '#2a2a2a' : '#f8fafc',
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 8,
                    fontSize: 13
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>
                        {item.plano_anterior ? `${item.plano_anterior} → ` : ''}
                        {item.plano_novo}
                      </span>
                      <span style={{ opacity: 0.7 }}>
                        {new Date(item.data_mudanca).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div style={{ opacity: 0.7 }}>
                      {item.motivo} 
                      {item.valor_pago > 0 && ` • R$ ${item.valor_pago.toFixed(2)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {activeSection === 'plano' && !plano && (
        <section className="account-card account-empty-state">
          <FaCrown />
          <div><h2>Carregando plano e utilização</h2><p>Estamos consultando os recursos atuais da sua conta.</p></div>
        </section>
      )}

      {/* Card de Perfil Original */}
      {activeSection === 'perfil' && (
      <section className="account-card account-personal-card" style={{
        background: darkMode ? '#232526' : '#fff',
        borderRadius: 16,
        boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px #1976d233',
        padding: '2rem 1.5rem'
      }}>
        <div className="account-section-heading">
          <span className="account-section-icon"><FaUser /></span>
          <div><h2 style={{ color: darkMode ? '#ffd54f' : '#1976d2', marginTop: 0 }}>Dados pessoais</h2><p>Mantenha seus dados de contato atualizados.</p></div>
        </div>
      <div className="account-form-grid">
        <label className="account-field"><span><FaUser /> Nome</span><input style={inputStyle} value={nome} onChange={e => setNome(e.target.value)} placeholder="Seu nome" disabled={loading} /></label>
        <AuthenticatedEmailField
          email={identityEmail}
          loading={authIdentityLoading}
          inputStyle={inputStyle}
        />
        <label className="account-field"><span><FaPhone /> Telefone</span><input style={inputStyle} value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="Seu telefone" disabled={loading} type="tel" /></label>
        <label className="account-field"><span><FaCalendarAlt /> Data de nascimento</span><input style={inputStyle} value={dataNascimento} onChange={e => setDataNascimento(e.target.value)} disabled={loading} type="date" /></label>
      </div>

      <button
        onClick={handleSalvar}
        style={{
          ...inputStyle,
          background: darkMode ? '#ffd54f' : '#1976d2',
          color: darkMode ? '#232526' : '#fff',
          fontWeight: 700,
          marginBottom: 20,
          cursor: loading ? 'not-allowed' : 'pointer'
        }}
        disabled={loading}
      >
        <FaSave /> {loading ? 'Salvando...' : 'Salvar alterações'}
      </button>

      {/* Mensagens */}
      {message && (
        <div style={{
          marginTop: 16,
          padding: 12,
          borderRadius: 8,
          background: '#d4edda',
          color: '#155724',
          fontSize: 14
        }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 16,
          padding: 12,
          borderRadius: 8,
          background: '#f8d7da',
          color: '#721c24',
          fontSize: 14
        }}>
          {error}
        </div>
      )}
      </section>
      )}

      {activeSection === 'preferencias' && (
        <section className="account-card account-preferences-card">
          <div className="account-section-heading">
            <span className="account-section-icon"><FaPalette /></span>
            <div><h2>Aparência e experiência</h2><p>Personalize o ClimoraX para trabalhar com mais conforto.</p></div>
          </div>
          <div className="account-preference-row">
            <div>
              <strong>Tema da interface</strong>
              <span>{darkMode ? 'O tema escuro está ativo.' : 'O tema claro está ativo.'}</span>
            </div>
            <button type="button" onClick={() => setDarkMode(value => !value)}>
              <FaPalette /> Usar tema {darkMode ? 'claro' : 'escuro'}
            </button>
          </div>
          <div className="account-preference-note">
            <FaBell />
            <div><strong>Notificações operacionais</strong><span>Alertas de tarefas, aprovações e manejo permanecem disponíveis na Central de Notificações.</span></div>
          </div>
        </section>
      )}

      {/* Card de Dispositivos */}
      {activeSection === 'seguranca' && (
      <section className="account-card account-devices-card" style={{
        background: darkMode ? '#232526' : '#fff',
        borderRadius: 16,
        boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px #1976d233',
        padding: '1.5rem',
        marginTop: '1.5rem'
      }}>
        <div className="account-section-heading">
          <span className="account-section-icon"><FaMobileAlt /></span>
          <div><h2 style={{ color: darkMode ? '#ffd54f' : '#1976d2', marginTop: 0 }}>Meus dispositivos</h2><p>Gerencie os aparelhos autorizados a acessar sua conta.</p></div>
        </div>
        <p style={{ color: darkMode ? '#aaa' : '#666', fontSize: 13, marginBottom: '1rem' }}>
          Dispositivos autorizados a acessar sua conta.
          {!planLoading && plano?.tipo !== 'starter' && (
            <> Limite do plano {getPlanName(plano?.tipo)}: <strong>{getLimiteDispositivos(plano?.tipo) ?? 'conforme contrato'}</strong>.</>
          )}
        </p>
        {dispositivos.length === 0 ? (
          <p style={{ color: darkMode ? '#aaa' : '#888', fontSize: 13 }}>Nenhum dispositivo cadastrado.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dispositivos.map(d => {
              const isAtual = d.device_fingerprint === fingerprintAtual;
              return (
                <div className={`account-device-row${isAtual ? ' is-current' : ''}`} key={d.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: darkMode ? '#2a2d2e' : '#f8fafc',
                  borderRadius: 10,
                  padding: '12px 14px',
                  border: isAtual
                    ? `2px solid ${darkMode ? '#ffd54f' : '#1976d2'}`
                    : `1px solid ${darkMode ? '#444' : '#e2e8f0'}`
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: darkMode ? '#f8fafc' : '#1e293b' }}>
                      {d.device_name || 'Dispositivo'}
                      {isAtual && (
                        <span style={{
                          marginLeft: 8,
                          fontSize: 11,
                          background: darkMode ? '#ffd54f' : '#1976d2',
                          color: darkMode ? '#232526' : '#fff',
                          borderRadius: 6,
                          padding: '2px 7px',
                          fontWeight: 700
                        }}>ESTE DISPOSITIVO</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? '#aaa' : '#888', marginTop: 3 }}>
                      Cadastrado: {new Date(d.criado_em).toLocaleDateString('pt-BR')} &nbsp;·&nbsp;
                      Último acesso: {new Date(d.ultimo_acesso).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  {!isAtual && (
                    <button
                      onClick={() => revogarDispositivo(d.id)}
                      disabled={loadingDispositivos}
                      title="Remover dispositivo"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#f44336',
                        fontSize: 18,
                        padding: '4px 8px',
                        borderRadius: 6
                      }}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
      )}

      {/* Card de Suporte */}
      {activeSection === 'ajuda' && (
      <section className="account-card account-support-card" style={{
        background: darkMode ? '#232526' : '#fff',
        borderRadius: 16,
        boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px #1976d233',
        padding: '1.5rem',
        marginTop: '1.5rem'
      }}>
        <div className="account-section-heading"><span className="account-section-icon"><FaHeadset /></span><div><h2 style={{ color: darkMode ? '#ffd54f' : '#1976d2', marginTop: 0 }}>Suporte</h2><p>Ajuda, tutoriais e contato com nossa equipe.</p></div></div>
        <p style={{ color: darkMode ? '#ccc' : '#555', fontSize: 14, marginBottom: '1rem' }}>
          Dúvidas, sugestões ou problemas? Entre em contato — respondemos o mais rápido possível.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => setVerAjuda(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              background: 'none',
              border: `1px solid ${darkMode ? '#ffd54f' : '#1976d2'}`,
              color: darkMode ? '#ffd54f' : '#1976d2',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            <FaQuestionCircle /> Central de Ajuda e Tutoriais
          </button>
          <a
            href={`mailto:climorax.app@gmail.com?subject=${encodeURIComponent('Suporte ClimoraX')}&body=${encodeURIComponent('Olá,\n\nPreciso de ajuda com:\n\n')}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              background: darkMode ? '#ffd54f' : '#1976d2',
              color: darkMode ? '#232526' : '#fff',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none'
            }}
          >
            ✉️ climorax.app@gmail.com
          </a>
        </div>
      </section>
      )}

      {verAjuda && <CentralAjuda onClose={() => setVerAjuda(false)} />}

      {/* Card Legal */}
      {activeSection === 'legal' && (
      <section className="account-card account-legal-card" style={{
        background: darkMode ? '#232526' : '#fff',
        borderRadius: 16,
        boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px #1976d233',
        padding: '1.5rem',
        marginTop: '1.5rem'
      }}>
        <div className="account-section-heading"><span className="account-section-icon"><FaFileContract /></span><div><h2 style={{ color: darkMode ? '#ffd54f' : '#1976d2', marginTop: 0 }}>Documentos legais</h2><p>Consulte termos e políticas vigentes.</p></div></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            onClick={() => setVerTermos(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'none', border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
              borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
              color: darkMode ? '#f8fafc' : '#1e293b', fontSize: 14, textAlign: 'left'
            }}
          >
            <span style={{ fontSize: 20 }}>📄</span>
            <div>
              <div style={{ fontWeight: 600 }}>Termos de Uso</div>
              <div style={{ fontSize: 12, color: darkMode ? '#aaa' : '#888', marginTop: 2 }}>Versão 1.0 – Junho de 2026</div>
            </div>
          </button>
          <a
            href="/politica-privacidade.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'none', border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
              borderRadius: 10, padding: '12px 16px',
              color: darkMode ? '#f8fafc' : '#1e293b', fontSize: 14,
              textAlign: 'left', textDecoration: 'none'
            }}
          >
            <span style={{ fontSize: 20 }}>🔒</span>
            <div>
              <div style={{ fontWeight: 600 }}>Política de Privacidade</div>
              <div style={{ fontSize: 12, color: darkMode ? '#aaa' : '#888', marginTop: 2 }}>Tratamento, proteção e direitos sobre seus dados</div>
            </div>
          </a>
        </div>
      </section>
      )}

      {verTermos && (
        <TermosDeUso
          userId={user?.id}
          onAceito={() => setVerTermos(false)}
          somenteVisualizacao
        />
      )}

      <button
        className="account-back-button"
        onClick={onBack}
        style={{
          width: '100%',
          minHeight: 48,
          padding: '0.8rem',
          margin: '1.5rem 0 2rem',
          borderRadius: 10,
          background: darkMode ? '#444' : '#eee',
          color: darkMode ? '#ffd54f' : '#1976d2',
          fontWeight: 700,
          fontSize: 15,
          border: 'none',
          cursor: 'pointer',
          touchAction: 'manipulation'
        }}
      >
        <FaArrowLeft /> Voltar ao painel
      </button>
    </div>
  );
}
